import React, { useState, useEffect } from "react";
import { styled, keyframes } from "@mui/material/styles";
import Typography, { TypographyProps } from "@mui/material/Typography";

// Define a type for the props
type TypingEffectProps = {
  text: string;
  typingSpeed?: number;
  deletingSpeed?: number;
  delay?: number;
  variant?: TypographyProps["variant"];
};

// Keyframes for the cursor blink
const blinkCaret = keyframes`
  50% { border-color: transparent }
`;

const StyledTypography = styled(Typography)(({ theme }) => ({
  paddingRight: "0.1em",
  borderColor: theme.palette.text.primary,
  animation: `${blinkCaret} 1s step-end infinite`,
}));

const TypingEffect: React.FC<TypingEffectProps> = ({
  text,
  typingSpeed = 150,
  deletingSpeed = 50,
  delay = 1000,
  variant = "h4",
}) => {
  const [displayedText, setDisplayedText] = useState("");
  const [index, setIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>; // Explicitly type the timeout

    if (deleting) {
      if (index > 1) {
        timeout = setTimeout(() => {
          setDisplayedText((currentText) => currentText.slice(0, -1));
          setIndex((currentIndex) => currentIndex - 1);
        }, deletingSpeed);
      } else {
        timeout = setTimeout(() => {
          setDeleting(false);
        }, delay);
      }
    } else {
      if (index < text.length) {
        timeout = setTimeout(() => {
          setDisplayedText((currentText) => currentText + text.charAt(index));
          setIndex((currentIndex) => currentIndex + 1);
        }, typingSpeed);
      } else {
        timeout = setTimeout(() => {
          setDeleting(true);
        }, delay);
      }
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [text, index, deleting, typingSpeed, deletingSpeed, delay]);

  return <StyledTypography variant={variant}>{displayedText}</StyledTypography>;
};

export default TypingEffect;
